import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FileText,
  CreditCard,
  GitCompare,
  CalendarClock,
  ListChecks,
  Users,
  BarChart3,
  ClipboardList,
  LayoutGrid,
} from 'lucide-react';
import { AppHeader } from '@/components/shared/AppHeader';
import { useAuth } from '@/contexts';
import { hasCentralRelatoriosAccess } from '@/features/relatorios/access';
import { motion } from 'framer-motion';

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      type: 'spring' as const,
      stiffness: 100,
      damping: 15,
    },
  },
};

const itemDisabled = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 0.5,
    y: 0,
    transition: {
      duration: 0.4,
      type: 'spring' as const,
      stiffness: 100,
      damping: 15,
    },
  },
};

/** O "Acessar →" que a decisão 5 pede em cada cartão que abre tela. */
const ACESSAR = <span className="font-medium text-accent">Acessar →</span>;

/** O selo dos cartões que ainda não existem (decisão 7). */
const EM_BREVE = (
  <span className="rounded bg-muted px-2 py-0.5 text-xs font-semibold text-muted-foreground">
    Em breve
  </span>
);

interface ModuleCardProps {
  to?: string;
  disabled?: boolean;
  icon: ReactNode;
  iconWrapperClassName: string;
  title: string;
  description: ReactNode;
  footerLeft: ReactNode;
  footerRight?: ReactNode;
  footerClassName?: string;
  badge?: ReactNode;
}

function ModuleCard({
  to,
  disabled,
  icon,
  iconWrapperClassName,
  title,
  description,
  footerLeft,
  footerRight,
  footerClassName,
  badge,
}: ModuleCardProps) {
  const card = (
    <Card
      className={
        disabled
          ? 'transition-all h-full pointer-events-none'
          : "relative overflow-hidden transition-all hover:border-accent hover:-translate-y-0.5 cursor-pointer h-full before:content-[''] before:absolute before:inset-y-0 before:-left-1/2 before:w-1/2 before:bg-linear-to-r before:from-transparent before:via-foreground/10 before:to-transparent before:opacity-0 before:pointer-events-none before:animate-none group-hover:before:animate-[card-shimmer_300ms_linear_forwards]"
      }
    >
      <CardHeader>
        <div className="flex items-center justify-between">
          <div
            className={
              'flex h-12 w-12 items-center justify-center rounded-lg ' + iconWrapperClassName
            }
          >
            {icon}
          </div>
          {badge && <div>{badge}</div>}
        </div>
        <CardTitle className="mt-4">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className={footerClassName ?? 'flex items-center justify-between text-sm'}>
          {footerLeft}
          {footerRight}
        </div>
      </CardContent>
    </Card>
  );

  if (to && !disabled) {
    return (
      <Link to={to} className="group">
        {card}
      </Link>
    );
  }

  return card;
}

export function HomePage() {
  const { user } = useAuth();
  const canAccessRelatorios = hasCentralRelatoriosAccess(user?.cnpj);

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />

      {/* Main Content */}
      <main className="container mx-auto px-6 pt-8 pb-20">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground text-balance mb-2">
            Sistema completo de apuração tributária
          </h2>
          <p className="text-muted-foreground text-pretty">Simplificando seu dia a dia</p>
        </div>

        {/* A ordem é a da decisão 4 da SPEC-017, e é a ordem do trabalho — não a do código antigo. */}
        <motion.div
          className="grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          variants={container}
          initial="hidden"
          animate="show"
        >
          {/* 1. Só cartão, sem tela: não leva a lugar nenhum de propósito (decisão 7). */}
          <motion.div variants={itemDisabled}>
            <ModuleCard
              disabled
              icon={<Users className="h-6 w-6 text-blue-600" />}
              iconWrapperClassName="bg-blue-500/10"
              title="Conhecendo Meus Fornecedores"
              description={
                'Integração com Receita Federal para validação de regularidade fiscal e créditos'
              }
              footerLeft={<span className="text-muted-foreground">Validação automática</span>}
            />
          </motion.div>

          <motion.div variants={item}>
            <ModuleCard
              to="/documentos"
              icon={<FileText className="h-6 w-6 text-green-600" />}
              iconWrapperClassName="bg-green-500/10"
              title="Documentos Fiscais"
              description={
                'Visualize NF-e, NFS-e, NFCom, NF3-e, NFGas e CT-e com tags da Lei 214/2025'
              }
              footerLeft={<span className="text-muted-foreground">Todos os modelos</span>}
              footerRight={ACESSAR}
            />
          </motion.div>

          <motion.div variants={item}>
            <ModuleCard
              to="/servicos-nfse"
              icon={<CreditCard className="h-6 w-6 text-yellow-600" />}
              iconWrapperClassName="bg-yellow-500/10"
              title="NFS-e - Serviços"
              description={
                'Gerenciamento completo de NFS-e (Tomados e Prestados) conforme NT 003/2025'
              }
              footerLeft={<span className="text-muted-foreground">Aceite e créditos</span>}
              footerRight={ACESSAR}
            />
          </motion.div>

          <motion.div variants={item}>
            <ModuleCard
              to="/apuracao-detalhada"
              icon={<ListChecks className="h-6 w-6 text-primary" />}
              iconWrapperClassName="bg-primary/10"
              title="Apuração Detalhada"
              description={
                'Resumo completo por CFOP, CST e Classificação Tributária com diferentes cenários'
              }
              footerLeft={<span className="text-muted-foreground">Entradas e Saídas</span>}
              footerRight={ACESSAR}
            />
          </motion.div>

          <motion.div variants={item}>
            <ModuleCard
              to="/gestao-impostos"
              icon={<BarChart3 className="h-6 w-6 text-purple-600" />}
              iconWrapperClassName="bg-purple-500/10"
              title="Gestão de Impostos"
              description={'Visão completa da carga tributária e simulação da transição até 2033'}
              footerLeft={<span className="text-muted-foreground">Reforma tributária</span>}
              footerRight={ACESSAR}
            />
          </motion.div>

          {/* 6. O selo "Em breve" substitui o "Status: Conforme" — que era cor sem texto a
              afirmar conformidade de um módulo que nem abre (decisões 7 e 8). */}
          <motion.div variants={itemDisabled}>
            <ModuleCard
              disabled
              icon={<GitCompare className="h-6 w-6 text-green-600" />}
              iconWrapperClassName="bg-green-500/10"
              title="Comparativo Comitê Gestor"
              description={'Compare valores apurados com dados do Comitê Gestor'}
              badge={EM_BREVE}
              footerLeft={<span className="text-muted-foreground">Conferência externa</span>}
            />
          </motion.div>

          {canAccessRelatorios && (
            <motion.div variants={item}>
              <ModuleCard
                to="/relatorios"
                icon={<ClipboardList className="h-6 w-6 text-secondary-foreground" />}
                iconWrapperClassName="bg-secondary"
                title="Central de Relatórios"
                description={
                  'Acesse, filtre e exporte relatórios fiscais de ICMS, PIS/COFINS, IPI, IBS/CBS e mais'
                }
                footerLeft={
                  <span className="text-muted-foreground">ICMS · PIS/COFINS · IBS/CBS</span>
                }
                footerRight={ACESSAR}
              />
            </motion.div>
          )}

          <motion.div variants={itemDisabled}>
            <ModuleCard
              disabled
              icon={<CalendarClock className="h-6 w-6 text-orange-600" />}
              iconWrapperClassName="bg-orange-500/10"
              title="Gestão de Obrigações Fiscais"
              description={'Acompanhamento dos prazos e das entregas das obrigações acessórias'}
              badge={EM_BREVE}
              footerLeft={<span className="text-muted-foreground">Prazos e entregas</span>}
            />
          </motion.div>

          <motion.div variants={item}>
            <ModuleCard
              to="/simples-nacional"
              icon={<LayoutGrid className="h-6 w-6 text-white" />}
              iconWrapperClassName="bg-gradient-to-br from-green-600 to-slate-900"
              title="Gestão do Simples Nacional"
              description="Sistema completo para empresas do Simples Nacional com apuração integrada de IBS/CBS"
              footerLeft={<span className="text-muted-foreground">Módulo completo</span>}
              footerRight={ACESSAR}
            />
          </motion.div>
        </motion.div>
      </main>
    </div>
  );
}
